import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ConfirmDialog } from 'components';
import {
  Grow,
  Icon,
  IconButton,
  TextField,
  Chip,
  Button,
  Tooltip,
  MenuItem,
  FormControl,
  Select
} from '@material-ui/core';
import {
  EditIcon,
  DeleteIcon,
} from 'components/CustomSVG';
import MUIDataTable from 'mui-datatables';
import {
  addTasksToQueueApi,
  getQuoteProStoreProductExportApi
} from 'api';
import {
  addToMassEdit
} from 'redux/actions/quote';
import logo from 'components/Logo/logo-dermpro.svg';
import { imgBaseUrl } from 'configs';
import { generateTaskToDeleteProduct } from 'helpers/queueTasks';
import useStyles from './styles';


const displayStatus = {
  'CREATE_PRODUCT': 'Creating...',
  'UPDATE_PRODUCT_TO_ADD_STOREVIEW': 'Creating...',
  'UPDATE_PRODUCT': 'Updating...',
  'DELETE_PRODUCT': 'Deleting...',
  'UPDATE_PRODUCT_TO_DELETE_STOREVIEW': 'Deleting...'
};

const QuoteProductsList = ({
  productsList = [],
  queueTasks = [],
  queueTasksSkus = [],
  tabValue
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory<{push:() => void}>();
  const location = useLocation();

  const [catalog, setCatalog] = useState([]); // TODO: check why it is called catalog?
  const [loaded, setLoaded] = useState(false);
  const [productToDelete, setProductDelete] = useState(null);

  const { allBrands } = useSelector((state: IReduxState) => state.brands);
  const {profile} = useSelector((state: IReduxState) => state.user);
  const {quoteGroupsAttributeData, quoteProductsExport, loadingQuoteProductsExport} = useSelector((state: IReduxState) => state.quote);

  const groups = quoteGroupsAttributeData?.options.filter((group) => !!group.value.length) || [];

  const searchParams = new URLSearchParams(location.search);
  const currentPage = searchParams.get('page');
  const rowsPerPage = searchParams.get('rowsPerPage');

  const permissions = profile?.config?.permissions || {};
  const userStore = profile?.stores?.[0];
  const storeViewCode = userStore.quoteProStoreCode;
  const mediaStoreUrl = `${userStore?.apiUrl}/media/catalog/product` || imgBaseUrl;
  const isQueueWorks = queueTasks.find((queue) => queue.status !== 'FAILED');

  useEffect(() => {
    if (!searchParams.has('page')) {
      searchParams.set('page', '0');

      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }

    if (!searchParams.has('rowsPerPage')) {
      searchParams.set('rowsPerPage', '20');

      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }
  }, [currentPage]);

  useEffect(() => {
    if (allBrands?.length) {
      setCatalog([...productsList.map((product) => {
        const productBrand = product?.custom_attributes?.find((attr) => attr.attribute_code === 'brand')?.value;
        const productImagePath = product?.custom_attributes?.find((attr) => attr.attribute_code === 'image')?.value;
        const currentGroupsIds = product?.custom_attributes.find((attr) => attr.attribute_code === 'catalog_quotepro_groups')?.value?.split(',') || [];

        return {
          ...product,
          image: productImagePath ? `${mediaStoreUrl}${productImagePath}` : logo,
          price: +Number.parseFloat(product?.price).toFixed(2) || '0',
          brand: allBrands?.find((b) => b.option_id === productBrand)?.value ||
            product?.custom_attributes?.find((attr) => attr.attribute_code === 'catalog_brand_name')?.value,
          status: product?.status === 1 ? 'Enabled' : 'Disabled',
          subscription_active: product?.custom_attributes?.find((attr) => attr.attribute_code === 'subscription_active')?.value || '0',
          groups: groups.flatMap((group) => currentGroupsIds.includes(group.value) ? group.label : []).join(',') || '0',
        };
      })]);
    }
  }, [
    productsList.length,
    allBrands,
    queueTasksSkus.length
  ]);

  useEffect(() => {
    if (!isQueueWorks) {
      getQuoteProStoreProductExportApi({ dispatch });
    }
  }, [isQueueWorks]);

  const openProductEdit = (sku = 'Product SKU', brand = 'Product Brand') => {
    history.push({pathname: `/quoting/listings/edit/${sku}`, search: `?brand=${brand}`});
  };

  const handleChangeProductStatus = (product) => {
    const editedProduct = { extension_attributes: product.extension_attributes, sku: product.sku, status: 2 };

    const task = {
      body: editedProduct,
      name: product.name,
      sku: product.sku,
      requestType: 'UPDATE_PRODUCT',
      storeViewCode
    };
    addTasksToQueueApi({ dispatch, tasks: [task] });
  };

  const handleConfirmDelete = () => {
    const task = generateTaskToDeleteProduct(
      productToDelete,
      userStore.storeId,
      userStore?.quoteProStoreId,
      storeViewCode
    );
    addTasksToQueueApi({dispatch, tasks: [task]});
  };

  const changeProductsStatus = async (selectedRows, productsList, tabValue) => {
    const selectedProdIdx = selectedRows.data.map((row) => row.dataIndex);
    const selectedProducts = selectedProdIdx.map((idx) => productsList[idx]);

    const tasks = selectedProducts.map((selectedProduct) => {
      const bodyData = {status: tabValue == 'active' ? 2 : 1};
      const task = {
        sku: selectedProduct.sku,
        name: selectedProduct.name,
        body: bodyData,
        requestType: 'UPDATE_PRODUCT',
        storeViewCode
      };

      return task;
    });

    await addTasksToQueueApi({ dispatch, tasks });
  };


  const handleBulkEditGroups = (selectedRows, productsList) => {
    const selectedProdIdx = selectedRows.data.map((row) => row.dataIndex);
    const selectedProducts = selectedProdIdx.map((idx) => productsList[idx]);

    dispatch(addToMassEdit(selectedProducts));

    history.push('/quoting/listings/bulk-edit');
  };

  const columns = [
    {
      name: 'name',
      label: 'Item',
      options: {
        sort: true,
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <div className="flex items-center">
              <img
                src={loaded ? product.image : logo}
                alt={product.name}
                style={{maxHeight: '60px', width: '60px', objectFit: 'contain'}}
                className="border-radius-8"
                onLoad={() => setLoaded(true)}
                onError={(e) => {
                  (e.target as HTMLImageElement).onerror = null;
                  (e.target as HTMLImageElement).src = logo;
                }}
              />
              <div className="ml-3">
                <p className="my-0 text-15" >{product.name}</p>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'groups',
      label: 'Groups',
      options: {
        filter: true,
        sort: true,
        filterType: 'custom',
        customFilterListOptions: {
          render: (val) => val[0] !== '0' ? val : 'None',
          update: (filterList, _filterPos, index) => {
            filterList[index] = [];
            return filterList;
          }
        },
        filterOptions: {
          logic: (group, filters) => {
            if (filters.length) {
              return !group.includes(filters);
            }

            return false;
          },
          renderValue: (val) => val !== '0' ? val : 'None',
          display: (filterList, onChange, index, column) => {
            return (
              <FormControl variant="standard">
                <Select
                  value={filterList[index]}
                  className="mt-4"
                  id="select"
                  displayEmpty
                  onChange={(event) => {
                    const { value } = event.target;
                    filterList[index][0] = value;
                    onChange(filterList[index], index, column);
                  }}
                >
                  <MenuItem disabled value="">
                    <span className="text-muted">Groups</span>
                  </MenuItem>
                  <MenuItem value="0">None</MenuItem>
                  {groups.map((item) => (<MenuItem key={item.label} value={item.label}>{item.label}</MenuItem>))}
                </Select>
              </FormControl>
            );
          }
        },
        setCellHeaderProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          const productGroups = product.groups.split(',').filter((el) => el !== '0');

          return (
            <div className="flex-column items-start">
              {productGroups.map((el) => (
                <Chip className="my-2 max-w-140" key={el} label={el} color={'default'} />
              ))}
            </div>
          );
        },
      },
    },
    {
      name: 'brand',
      label: 'Brand',
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          const productBrandId = product?.custom_attributes?.find((attr) => attr.attribute_code === 'brand')?.value;
          const brand = product?.brand || allBrands.find((brand) => brand.id == productBrandId)?.value;

          return (
            <div className="flex items-center">
              <p className="my-0 text-15 capitalize">{brand}</p>
            </div>
          );
        },
      },
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ style: { width: '15%' }, className: 'whitespace-pre'}),
        setCellProps: () => ({className: 'whitespace-pre'}),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <div className="flex items-center">
              {`$${Number(product.price) ? product.price.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') : product.price}`}
            </div>
          );
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: false,
        sort: true,
        filterType: 'dropdown',
        setCellHeaderProps: () => ({ style: { width: '15%', paddingLeft: 20}}),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <Chip label={product.status} color={product.status === 'Enabled' ? 'primary' : 'default'} />
          );
        },
      },
    },
    {
      name: 'action',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ className: 'w-120', padding: 0 }),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex] || {};
          const isQueued = queueTasksSkus.find((sku) => sku === product.sku);

          if (isQueued) {
            const task = queueTasks.find((t) => t.sku === product.sku);

            if (!task.error) {
              return (
                <div className="flex items-center">
                  {displayStatus[task.requestType]}
                </div>
              );
            }
          }

          const handleDeleteClick = (product) => {
            if (product.status === 'Enabled') {
              handleChangeProductStatus(product);
            } else {
              setProductDelete(product);
            }
          };

          return (
            <div className="flex items-center">
              <IconButton
                title="Click here to make edits to your item (e.g.: price, groups, discounts etc.)"
                disabled={isQueued}
                onClick={() => {
                  openProductEdit(product?.sku, product?.brand);
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                title="Click here to delete the product from your store"
                disabled={isQueued}
                onClick={() => handleDeleteClick(product)}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          );
        },
      },
    },
  ];

  return (
    <>
      <MUIDataTable
        className={classes.root}
        data={catalog}
        columns={columns}
        options={{
          filterArrayFullMatch: false,
          filterType: 'textField',
          responsive: 'standard',
          selectableRows: permissions.massEditActive ? 'multiple' : 'none',
          search: true,
          filter: true,
          download: loadingQuoteProductsExport || isQueueWorks  ? 'disabled' : profile.isDermproStaff,
          downloadOptions: {
            filename: 'quotePro-listings.csv'
          },
          textLabels: {
            toolbar: {
              downloadCsv: 'Click here to download your current SKUs in an Excel spreadsheet'
            }
          },
          onDownload: (buildHead, buildBody, columns) => {
            const data = quoteProductsExport.products;
            const columnLength = columns.length;

            // deleting grid columns
            for (let i = 0; i < columnLength; i++) {
              columns.shift();
            }

            quoteProductsExport.columns.forEach((column) => {
              columns.push({
                download: true,
                name: column,
                label: column
              });
            });

            return `${buildHead(columns)}${buildBody(data)}`;
          },
          print: false,
          pagination: true,
          viewColumns: true,
          elevation: 0,
          searchPlaceholder: 'Enter product name',
          rowsPerPage: Number(rowsPerPage),
          rowsPerPageOptions: [20, 40, 80, 100],
          page: Number(currentPage),
          onChangePage: (page) => {
            history.push({pathname: location.pathname, search: `?page=${page}&rowsPerPage=${rowsPerPage}`});
          },
          onChangeRowsPerPage: (rows) => {
            history.push({pathname: location.pathname, search: `?page=0&rowsPerPage=${rows}`});
          },
          isRowSelectable: (dataIndex) => {
            const isQueued = queueTasksSkus.includes(catalog[dataIndex].sku);
            return !isQueued;
          },
          customToolbarSelect: (selectedRows) => (
            <div>
              <Button
                className="mr-3"
                variant="contained"
                color="primary"
                onClick={() => handleBulkEditGroups(selectedRows, productsList)}
              >
                Bulk Edit
              </Button>
              <Tooltip title={`Transfer to ${tabValue == 'active' ? 'disabled' : 'active'} tab`} className="mr-2">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => changeProductsStatus(selectedRows, productsList, tabValue)}
                >
                  {`Transfer to ${tabValue == 'active' ? 'disabled' : 'active'}`}
                </Button>
              </Tooltip>
            </div>
          ),
          customSearchRender: (
            searchText,
            handleSearch,
            hideSearch
          ) => {
            return (
              <Grow appear in timeout={300}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  onChange={({ target: { value } }) =>
                    handleSearch(value)
                  }
                  InputProps={{
                    style: {
                      paddingRight: 0,
                    },
                    startAdornment: (
                      <Icon
                        className="mr-2"
                        fontSize="small"
                      >
                        search
                      </Icon>
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={hideSearch}
                      >
                        <Icon fontSize="small">
                          clear
                        </Icon>
                      </IconButton>
                    ),
                  }}
                />
              </Grow>
            );
          },
          onTableChange: (action, state) => {
            if (action === 'propsUpdate') {
              state.selectedRows = {
                data: [],
                lookup: {},
              };
            }
          },
        }}
      />
      <ConfirmDialog
        title="Delete product?"
        open={Boolean(productToDelete)}
        setOpen={setProductDelete}
        onConfirm={handleConfirmDelete}
      >
        Are you sure you want to delete the product from your quote listings?
      </ConfirmDialog>
    </>
  );
};

export default QuoteProductsList;
